import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type CommTemplateData, CommTemplateForm, type CommTemplateInput } from './CommTemplateForm';

export const Route = createFileRoute('/admin-console/platform-content/comms-and-content/comm-templates/add')({
    component: AddCommTemplateRoute,
    staticData: {
        presentation: 'overlay',
    },
});

function AddCommTemplateRoute() {
    const { isReadOnly } = useAdminModulePermissions('comms');

    if (isReadOnly) {
        return <Navigate to="/admin-console/platform-content/comms-and-content/comm-templates" />;
    }

    return (
        <PageController route={Route}>
            <AddCommTemplate />
        </PageController>
    );
}

function AddCommTemplate() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const [data, setData] = useState<CommTemplateData>({
        name: '',
        internalName: '',
        channel: 'EMAIL',
        template: '',
        subjectTemplate: '',
        relatedTo: undefined,
    });

    const { mutate: createCommTemplate, isPending: isCreating } = trpc.commTemplate.createCommTemplate.useMutation();

    const handleSubmit = (values: CommTemplateInput) => {
        setData(values);
        const key = 'create_comm_template';
        message.loading({ content: 'Creating…', key });
        createCommTemplate(values, {
            onSuccess: () => {
                message.success({ content: 'Comm template created', key });
                trpcUtils.commTemplate.listCommTemplates.invalidate();
                setOpen(false);
            },
            onError: (error) => {
                errorMessage.show(error);
            },
        });
    };

    return (
        <CommTemplateForm
            open={open}
            mode="add"
            data={data}
            state={match({ isCreating })
                .with({ isCreating: true }, () => 'submitting' as const)
                .otherwise(() => 'ready' as const)}
            onSubmit={handleSubmit}
            onCloseFinished={() => {
                navigate({
                    to: '/admin-console/platform-content/comms-and-content/comm-templates',
                });
            }}
        />
    );
}
